/**
 * A `w:lvlText` template as the two halves CSS can put either side of a counter.
 *
 * The counterpart of `renderListMarker`, which fills the same template for the PDF. The editor
 * cannot call that — a browser counter is what renumbers a list as the author edits it — so the
 * template is split instead: `(%1)` becomes `(` + counter + `)`.
 *
 * A template naming more than one level (`%1.%2`) cannot be one counter, so the separator
 * between its last two references is reported and the caller uses `counters()` instead.
 */
export interface MarkerTemplateParts {
	prefix: string;
	suffix: string;
	/** Present when the template states more than one level. */
	nestedSeparator?: string;
}

const REFERENCE = /%\d/g;

export const markerTemplateParts = (template: string): MarkerTemplateParts => {
	const references = [...template.matchAll(REFERENCE)];
	const last = references[references.length - 1];
	if (last === undefined || last.index === undefined) {
		// No `%N` at all: the whole template is a literal, so there is nothing to count.
		return { prefix: template, suffix: '' };
	}
	const suffix = template.slice(last.index + last[0].length);
	const previous = references[references.length - 2];
	if (previous === undefined || previous.index === undefined) {
		return { prefix: template.slice(0, last.index), suffix };
	}
	const first = references[0];
	return {
		prefix: first?.index === undefined ? '' : template.slice(0, first.index),
		suffix,
		nestedSeparator: template.slice(previous.index + previous[0].length, last.index),
	};
};

/** A CSS `content` string literal. Quotes and backslashes escaped so a glyph cannot break out. */
export const cssStringLiteral = (value: string): string =>
	`'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
